import React, { useContext, useId, useState } from 'react';
import { ApiContext } from '@api/api';
import { ExpeditionPreviewOutDTO, OpeningModeEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import SvgRoute from '@icons/route.svg?react';
import SvgHelp from '@icons/help.svg?react';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';
import dayjs, { Dayjs } from 'dayjs';
import { isNotNullOrUndefined, parseNumberOrUndefined } from '@utils/utils.tsx';

type ShiftUnit = 'day' | 'week';

/**
 * The hairline the design puts under the header block and between table rows. A plain element
 * rather than a component: this file exports a hook, and a module-level component alongside it
 * would cost the file its fast refresh. Elements are immutable, so rendering it repeatedly is fine.
 */
const divider = <div className={'h-px w-full bg-content-quaternary'} />;

/**
 * Bulk multiduplication v3: shift the selected expeditions by N days/weeks. Creates a DRAFT
 * async job (validation runs in the background) and navigates to the draft detail page.
 */
export const useCreateMultiduplicationSidebar = () => {
  const [visible, setVisible] = useState(false);

  interface ComponentProps {
    expeditions: ExpeditionPreviewOutDTO[];
    onCreated?: () => void;
  }

  const MultiduplicationSidebar: React.FC<ComponentProps> = ({ expeditions, onCreated }) => {
    const { createMultiduplicationV3Job } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const nav = useNavigation();
    const [amount, setAmount] = useState('1');
    const [unit, setUnit] = useState<ShiftUnit>('week');
    const [saving, setSaving] = useState(false);
    const helpId = `multiduplication-help-${useId().replace(/:/g, '-')}`;

    const shiftDays = (parseNumberOrUndefined(amount) ?? 0) * (unit === 'week' ? 7 : 1);

    // First/last open day of the first location — same fields the list's "Trasa" column renders.
    const originalRange = (row: ExpeditionPreviewOutDTO): [Dayjs, Dayjs] | null => {
      const days = (row.locations?.[0]?.timeslots?.dayWithInterval ?? []).filter(d => d.openingMode !== OpeningModeEnum.CLOSED);
      if (days.length === 0) return null;
      const parse = (d: (typeof days)[number]) => dayjs(d.utcDay ?? d.day);
      return [parse(days[0]), parse(days[days.length - 1])];
    };

    const formatRange = (range: [Dayjs, Dayjs] | null, shift: number): string => {
      if (range == null) return '-';
      const [from, till] = range.map(d => d.add(shift, 'day'));
      return from.isSame(till, 'day') ? from.format('D. M. YYYY') : `${from.format('D. M.')} – ${till.format('D. M. YYYY')}`;
    };

    const create = () => {
      if (shiftDays === 0 || saving) return;
      setSaving(true);
      createMultiduplicationV3Job(
        { expeditionIds: expeditions.map(e => e.id), shiftDays },
        d => {
          setSaving(false);
          setVisible(false);
          onCreated?.();
          nav.navigate(nav.urlFunctions.createJobDetail(d.jobId, JobReferrer.EXPEDITIONS));
        },
        null,
        {
          successToast: false,
          onError: (error, showStandardError) => {
            showStandardError();
            setSaving(false);
          },
        },
      );
    };

    return (
      <RingilSidebar
        visible={visible}
        heading={tr('MultiduplicationSidebar.title', 'Duplikace přepravy')}
        onBack={() => setVisible(false)}
        onContinue={create}
        continueLabel={tr('MultiduplicationSidebar.duplicate', 'Duplikovat')}
        loading={saving}
        showSaveButton={expeditions.length > 0 && shiftDays !== 0}
        dataTestIdBack="multiduplication-close"
        dataTestIdContinue="multiduplication-create"
      >
        <div className={'flex flex-col gap-6'}>
          <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-row items-start gap-4'}>
              <SvgRoute className={'mt-1 h-4 w-4 shrink-0 text-content-tertiary'} />
              <div className={'flex flex-col gap-2'}>
                <span className={'text-sm font-medium leading-3 text-content-secondary'}>{tr('MultiduplicationSidebar.forExpeditions', 'Pro přepravy')}</span>
                <span className={'font-bold text-content-primary'}>{expeditions.map(e => e.applicationId ?? e.id).join(', ')}</span>
              </div>
            </div>
            {divider}
          </div>

          <div className={'flex flex-col gap-1'}>
            <div className={'flex flex-row items-center gap-2'}>
              <span className={'text-sm font-medium leading-3 text-content-secondary'}>{tr('MultiduplicationSidebar.shiftBy', 'Posunout vybrané přepravy o')}</span>
              <span id={helpId} className={'inline-flex cursor-help'}>
                <SvgHelp className={'h-4 w-4'} />
              </span>
              <CustomTooltip
                targetSelector={`#${helpId}`}
                content={tr(
                  'MultiduplicationSidebar.shiftByHelp',
                  'Každá kopie se vytvoří posunutá o zadanou dobu oproti původní přepravě. Náhled níže ukazuje výsledné termíny.',
                )}
              />
            </div>
            {/* The two controls read as one field in the design: the amount keeps only its left
                corners, the unit only its right, and the shared edge is not doubled. */}
            <div className={'flex flex-row'}>
              <InputText
                value={amount}
                onChange={e => setAmount(e.target.value)}
                keyfilter={'pint'}
                className={'flex-1 !rounded-r-none !border-r-0'}
                data-testid={'multiduplication-amount'}
              />
              <Dropdown
                value={unit}
                options={[
                  { label: tr('MultiduplicationSidebar.unitDay', 'den'), value: 'day' },
                  { label: tr('MultiduplicationSidebar.unitWeek', 'týden'), value: 'week' },
                ]}
                onChange={e => setUnit(e.value)}
                className={'w-[179px] !rounded-l-none'}
              />
            </div>
          </div>

          <div className={'flex flex-col'}>
            <div className={'flex flex-row gap-4 py-2 text-[11px] font-bold text-content-primary'}>
              <span className={'w-[132px]'}>{tr('MultiduplicationSidebar.expedition', 'Přeprava')}</span>
              <span className={'w-[160px]'}>{tr('MultiduplicationSidebar.originalStart', 'Začátek výchozí přepravy')}</span>
              <span className={'flex-1'}>{tr('MultiduplicationSidebar.newStart', 'Začátek duplikované přepravy')}</span>
            </div>
            {expeditions.map((row, index) => {
              const range = originalRange(row);
              return (
                <React.Fragment key={row.id}>
                  {index > 0 && divider}
                  <div className={'flex flex-row items-center gap-4 py-2 text-[15px] text-content-primary'}>
                    <span className={'w-[132px]'}>{row.applicationId ?? row.id}</span>
                    <span className={'w-[160px]'}>{formatRange(range, 0)}</span>
                    <span className={'flex-1'}>{isNotNullOrUndefined(range) && shiftDays !== 0 ? formatRange(range, shiftDays) : '-'}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </RingilSidebar>
    );
  };

  return { MultiduplicationSidebar, visible, setVisible };
};
